import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { CustomerSheet } from "@/components/sales/CustomerSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { formatKr, formatDate, formatOrg } from "@/lib/format";
import { toast } from "sonner";
import { Contact, Plus, Search, Pencil, Trash2, Building2, User, Receipt, Mail } from "lucide-react";

export default function CustomersPage() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState({ open: false, customer: null });
  const [del, setDel] = useState(null);

  const load = () => api.get("/sales/customers").then((r) => setItems(r.data.items)).catch((e) => setError(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const s = q.trim().toLowerCase();
    return s ? items.filter((c) => [c.name, c.email, c.org_number, c.contact_person, c.city].some((v) => (v || "").toLowerCase().includes(s))) : items;
  }, [items, q]);

  const remove = async () => {
    try {
      const r = await api.delete(`/sales/customers/${del.id}`);
      toast.success(r.data.deactivated ? "Kunden ble deaktivert (har utstedte fakturaer)" : "Kunde slettet");
      setDel(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Kunder" description="Kunderegisteret ditt. Kunder med utstedte fakturaer kan ikke slettes, bare deaktiveres." actions={<Button onClick={() => setSheet({ open: true, customer: null })} data-testid="customer-create-button"><Plus className="h-4 w-4 mr-2" /> Ny kunde</Button>} />

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk etter navn, e-post eller org.nr" className="pl-9" data-testid="customer-search-input" aria-label="Søk i kunder" />
      </div>

      {error ? <EmptyState title="Kunne ikke laste kunder" description={error} actionLabel="Prøv igjen" onAction={load} /> : !items ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Contact} title={q ? "Ingen treff" : "Ingen kunder ennå"} description={q ? "Prøv et annet søk." : "Legg inn firmaer og privatpersoner du fakturerer. Kundenummer tildeles automatisk."} actionLabel={q ? undefined : "Legg til første kunde"} onAction={() => setSheet({ open: true, customer: null })} testId="customers-empty-state" />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="customer-list">
          {filtered.map((c) => (
            <li key={c.id}>
              <Card className="h-full card-lift" data-testid={`customer-card-${c.id}`}>
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start gap-3">
                    <span className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">{c.type === "person" ? <User className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">Kundenr {c.customer_number}{c.org_number ? ` · Org.nr ${formatOrg(c.org_number)}` : ""}</p>
                    </div>
                    <div className="flex gap-0.5 -mr-2 -mt-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSheet({ open: true, customer: c })} aria-label="Rediger" data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDel(c)} aria-label="Slett" data-testid={`customer-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                    {c.contact_person ? <p className="truncate">v/ {c.contact_person}</p> : null}
                    {c.email ? <p className="truncate flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {c.email}</p> : <p className="text-xs text-warn">Mangler e-post for sending</p>}
                    {c.city ? <p className="truncate">{c.postal_code} {c.city}</p> : null}
                  </div>
                  <div className="mt-4 pt-3 border-t flex items-center justify-between text-sm">
                    <div><p className="text-xs text-muted-foreground">Fakturert</p><p className="font-medium tnum">{formatKr(c.invoiced_total)}</p></div>
                    <div className="text-right"><p className="text-xs text-muted-foreground">Utestående</p><p className={`font-medium tnum ${c.outstanding > 0 ? "text-foreground" : "text-muted-foreground"}`}>{formatKr(c.outstanding)}</p></div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" asChild><Link to={`/faktura/fakturaer?customer_id=${c.id}`} data-testid={`customer-invoices-${c.id}`}><Receipt className="h-3.5 w-3.5 mr-1.5" /> {c.invoice_count} {c.invoice_count === 1 ? "faktura" : "fakturaer"}</Link></Button>
                    <Button size="sm" className="flex-1" asChild><Link to={`/faktura/fakturaer/ny?customer_id=${c.id}`} data-testid={`customer-new-invoice-${c.id}`}><Plus className="h-3.5 w-3.5 mr-1.5" /> Ny faktura</Link></Button>
                  </div>
                  {c.last_invoice_date ? <p className="mt-2 text-[11px] text-muted-foreground">Sist fakturert {formatDate(c.last_invoice_date)}</p> : null}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <CustomerSheet open={sheet.open} onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))} customer={sheet.customer} onSaved={load} />

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Slette {del?.name}?</AlertDialogTitle><AlertDialogDescription>Har kunden utstedte fakturaer, blir den deaktivert i stedet (bokføringsplikt). Kladder påvirkes ikke.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="customer-delete-cancel">Avbryt</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="customer-delete-confirm">Slett</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
